import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { translateMenuTerm } from '@/i18n/translateMenuContent';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function CheckoutCartSummary({ cart, removeFromCart, updateQuantity }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">{t('checkout.orderSummary')}</h2>
      <div className="space-y-4">
        {cart.map((item) => (
          <div key={item.id} className="flex gap-4 pb-4 border-b last:border-0">
            {item.product.photo_url && (
              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={item.product.photo_url}
                  alt={translateMenuTerm(item.product.name, lng)}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">{translateMenuTerm(item.product.name, lng)}</h3>
                {item.isClubReward ? (
                  <Badge variant="secondary" className="text-xs">
                    {t('checkout.rewardRedeemed')}
                  </Badge>
                ) : null}
              </div>
              {item.addOns.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  +{' '}
                  {item.addOns
                    .map((a) => {
                      const qty = Number(a.quantity) || 1;
                      const name = translateMenuTerm(a.name, lng);
                      return qty > 1 ? `${name} x${qty}` : name;
                    })
                    .join(', ')}
                </p>
              )}
              {item.removedIngredients.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('catalog.without')}{' '}
                  {item.removedIngredients.map((ing) => translateMenuTerm(ing, lng)).join(', ')}
                </p>
              )}
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={item.isClubReward}
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  >
                    -
                  </Button>
                  <span className="w-8 text-center">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={item.isClubReward}
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  >
                    +
                  </Button>
                </div>
                <span className="font-semibold">
                  {item.isClubReward
                    ? '🎁 $0'
                    : `$${(item.price * item.quantity).toLocaleString()}`}
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              disabled={item.isClubReward}
              title={
                item.isClubReward
                  ? 'El premio ya fue canjeado; no se puede quitar desde acá.'
                  : undefined
              }
              onClick={() => removeFromCart(item.id)}
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
